// Word list — searchable table with category badges, inline category cycling,
// edit dialog (shadcn) and delete. The edit dialog now lives as a top-level
// component so its inputs are never remounted between renders.

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Trash, PencilSimple, MagnifyingGlass, FloppyDisk, X, Plus, Sparkle,
} from '@phosphor-icons/react';
import {
  useStore, wordsApi, CATEGORY_LABEL, CATEGORY_TONE, CATEGORIES,
} from '../lib/store';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { LIST } from '../constants/testIds';

const TYPE_LABEL = { noun: 'İsim', adjective: 'Sıfat', adverb: 'Zarf/Edat', verb: 'Fiil' };
const TYPE_TONE = {
  noun: 'text-blue-600 dark:text-blue-400 border-blue-500/40',
  adjective: 'text-amber-700 dark:text-amber-400 border-amber-500/40',
  adverb: 'text-purple-700 dark:text-purple-400 border-purple-500/40',
  verb: 'text-rose-700 dark:text-rose-400 border-rose-500/40',
};

const CAT_BADGE = {
  learned: 'bg-emerald-600 text-white border-emerald-600',
  review:  'bg-sky-400 text-black border-sky-400',
  unknown: 'bg-rose-600 text-white border-rose-600',
  new:     'bg-orange-500 text-black border-orange-500',
};

export default function WordListPage() {
  const { words } = useStore();
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [catFilter, setCatFilter] = useState('all');

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return words.filter((w) => {
      if (catFilter !== 'all' && (w.category || 'new') !== catFilter) return false;
      if (!t) return true;
      return (
        (w.german || '').toLowerCase().includes(t) ||
        (w.turkish || '').toLowerCase().includes(t) ||
        (w.type || '').toLowerCase().includes(t)
      );
    });
  }, [words, q, catFilter]);

  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-12 py-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8">
        <div>
          <div className="overline mb-3">KARTOTHEK · {words.length} EINTRÄGE</div>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none">
            Kelime Listesi
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid={LIST.searchInput}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ara…"
              className="rounded-none h-11 pl-9 w-56"
            />
          </div>
          {words.length > 0 && <ClearAllDialog />}
          <Button asChild className="rounded-none h-11">
            <Link to="/add"><Plus size={16} weight="bold" className="mr-1.5" /> Ekle</Link>
          </Button>
        </div>
      </div>

      {/* Category filter pills */}
      <div className="mb-6 flex items-center gap-2 flex-wrap text-xs">
        <FilterPill active={catFilter === 'all'} onClick={() => setCatFilter('all')} label={`Hepsi (${words.length})`} />
        {CATEGORIES.map((c) => {
          const n = words.filter((w) => (w.category || 'new') === c).length;
          return (
            <FilterPill
              key={c}
              active={catFilter === c}
              onClick={() => setCatFilter(c)}
              label={`${CATEGORY_LABEL[c]} (${n})`}
              tone={CAT_BADGE[c]}
            />
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <EmptyTable totalWords={words.length} />
      ) : (
        <div data-testid={LIST.table} className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left overline">
                <th className="py-3 px-4">Deutsch</th>
                <th className="py-3 px-4">Türkçe</th>
                <th className="py-3 px-4">Typ</th>
                <th className="py-3 px-4">Kategori</th>
                <th className="py-3 px-4 text-center">Bilg.</th>
                <th className="py-3 px-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 500).map((w) => (
                <WordRow key={w.id} word={w} onEdit={() => setEditing(w)} />
              ))}
            </tbody>
          </table>
          {filtered.length > 500 && (
            <div className="p-4 text-center text-xs text-muted-foreground">
              {filtered.length - 500} kelime daha (arama veya filtreyle daralt)…
            </div>
          )}
        </div>
      )}

      <EditDialog word={editing} onClose={() => setEditing(null)} />
    </section>
  );
}

// ---------- Row -----------------------------------------------------------
function WordRow({ word, onEdit }) {
  const cat = word.category || 'new';
  const isStub = !word.turkish;
  return (
    <tr
      data-testid={LIST.row(word.id)}
      className="border-b border-border hover:bg-muted/30 transition-colors"
    >
      <td className="py-4 px-4 align-top">
        <div className="font-display font-bold text-lg leading-tight">
          {word.type === 'noun' && word.article ? (
            <span className="text-muted-foreground mr-1">{word.article}</span>
          ) : null}
          {word.german}
        </div>
        {word.type === 'noun' && word.plural && (
          <div className="text-xs text-muted-foreground mt-0.5">Pl. {word.plural}</div>
        )}
      </td>
      <td className="py-4 px-4 align-top">
        {isStub
          ? <span className="inline-flex items-center gap-1 text-xs text-muted-foreground font-mono-plex">
              <Sparkle size={12} weight="duotone" /> KI bekleniyor
            </span>
          : word.turkish}
      </td>
      <td className="py-4 px-4 align-top">
        <span className={`inline-block text-[10px] tracking-[0.2em] uppercase font-semibold border px-2 py-1 ${TYPE_TONE[word.type] || ''}`}>
          {TYPE_LABEL[word.type] || word.type}
        </span>
      </td>
      <td className="py-4 px-4 align-top">
        <span className={`inline-block text-[10px] tracking-[0.2em] uppercase font-semibold border px-2 py-1 ${CAT_BADGE[cat]}`}>
          {CATEGORY_LABEL[cat]}
        </span>
      </td>
      <td className="py-4 px-4 align-top text-center font-mono-plex text-xs">
        <span className="text-emerald-600 dark:text-emerald-400">{word.knownCount || 0}</span>
        <span className="text-muted-foreground"> / </span>
        <span className="text-rose-600 dark:text-rose-400">{word.unknownCount || 0}</span>
      </td>
      <td className="py-4 px-4 align-top text-right">
        <div className="inline-flex gap-2">
          <button
            data-testid={LIST.editBtn(word.id)}
            onClick={onEdit}
            className="w-9 h-9 border border-border hover:border-foreground btn-tactile grid place-items-center"
            aria-label="Düzenle"
          >
            <PencilSimple size={16} weight="bold" />
          </button>
          <button
            data-testid={LIST.deleteBtn(word.id)}
            onClick={() => { wordsApi.remove(word.id); toast.success('Silindi'); }}
            className="w-9 h-9 border border-border hover:border-rose-500 hover:text-rose-500 btn-tactile grid place-items-center"
            aria-label="Sil"
          >
            <Trash size={16} weight="bold" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------- Filter pill ----------------------------------------------------
function FilterPill({ active, onClick, label, tone }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 border tracking-wide transition-colors ${
        active
          ? (tone || 'bg-foreground text-background border-foreground')
          : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );
}

// ---------- Empty state ---------------------------------------------------
function EmptyTable({ totalWords }) {
  return (
    <div className="border border-dashed border-border p-16 text-center">
      <p className="text-muted-foreground mb-4">
        {totalWords === 0 ? 'Henüz kelime eklenmedi.' : 'Bu filtre/arama için sonuç yok.'}
      </p>
      {totalWords === 0 && (
        <Button asChild className="rounded-none">
          <Link to="/add">İlk Kelimeyi Ekle</Link>
        </Button>
      )}
    </div>
  );
}

// ---------- Clear-all dialog ----------------------------------------------
function ClearAllDialog() {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          data-testid={LIST.clearAllBtn}
          className="rounded-none h-11 border-rose-500 text-rose-500 hover:bg-rose-500 hover:text-white"
        >
          <Trash size={16} weight="bold" className="mr-1.5" /> Temizle
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle>Tüm kelimeleri sil?</AlertDialogTitle>
          <AlertDialogDescription>
            Bu işlem geri alınamaz. Kart havuzundaki bütün kelimeler kaldırılacak
            (sonradan B2 sözlüğünü yeniden yükleyebilirsin).
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none">İptal</AlertDialogCancel>
          <AlertDialogAction
            className="rounded-none bg-rose-500 hover:bg-rose-600"
            onClick={() => { wordsApi.clearAll(); toast.success('Tüm kelimeler silindi'); }}
          >
            Evet, sil
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------- Edit dialog (top-level so inputs keep focus) -------------------
function FormField({ label, value, onChange, multiline = false, rows = 3 }) {
  return (
    <div>
      <label className="overline mb-1 block">{label}</label>
      {multiline
        ? <Textarea rows={rows} value={value || ''} onChange={onChange} className="rounded-none font-mono-plex" />
        : <Input value={value || ''} onChange={onChange} className="rounded-none" />}
    </div>
  );
}

function EditDialog({ word, onClose }) {
  const [draft, setDraft] = useState(null);

  // Sync draft whenever a new word is opened
  React.useEffect(() => {
    if (!word) { setDraft(null); return; }
    setDraft({
      turkish: word.turkish || '',
      article: word.article || '',
      plural: word.plural || '',
      sentence: word.sentence || '',
      sentences: (word.sentences || []).join('\n'),
      praesens: word.praesens || '',
      praeteritum: word.praeteritum || '',
      perfekt: word.perfekt || '',
      category: word.category || 'new',
      type: word.type,
    });
  }, [word]);

  if (!word || !draft) return null;

  const save = () => {
    const patch = { turkish: draft.turkish, category: draft.category, type: draft.type };
    if (draft.type === 'noun') {
      patch.article = draft.article;
      patch.plural = draft.plural;
      patch.sentence = draft.sentence;
    } else if (draft.type === 'verb') {
      patch.praesens = draft.praesens;
      patch.praeteritum = draft.praeteritum;
      patch.perfekt = draft.perfekt;
    } else {
      patch.sentences = draft.sentences.split('\n').map((s) => s.trim()).filter(Boolean);
    }
    wordsApi.update(word.id, patch);
    toast.success('Güncellendi');
    onClose();
  };

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  return (
    <Dialog open={!!word} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="rounded-none max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="overline mb-1">Düzenle · {TYPE_LABEL[word.type]}</div>
          <DialogTitle className="font-display font-black text-3xl tracking-tighter">
            {word.german}
          </DialogTitle>
          <DialogDescription>
            Kelimenin detaylarını ve spaced-repetition kategorisini güncelle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField label="Türkçe" value={draft.turkish} onChange={set('turkish')} />

          <div>
            <label className="overline mb-1 block">Kategori</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, category: c }))}
                  className={`h-10 text-xs border tracking-wide transition-colors ${
                    draft.category === c
                      ? CAT_BADGE[c]
                      : 'border-border text-muted-foreground hover:border-foreground'
                  }`}
                >
                  {CATEGORY_LABEL[c]}
                </button>
              ))}
            </div>
          </div>

          {draft.type === 'noun' && (
            <>
              <FormField label="Artikel (der/die/das)" value={draft.article} onChange={set('article')} />
              <FormField label="Plural" value={draft.plural} onChange={set('plural')} />
              <FormField label="B2 Cümle" value={draft.sentence} onChange={set('sentence')} multiline />
            </>
          )}
          {draft.type === 'verb' && (
            <>
              <FormField label="Präsens" value={draft.praesens} onChange={set('praesens')} />
              <FormField label="Präteritum" value={draft.praeteritum} onChange={set('praeteritum')} />
              <FormField label="Perfekt" value={draft.perfekt} onChange={set('perfekt')} />
            </>
          )}
          {(draft.type === 'adjective' || draft.type === 'adverb') && (
            <FormField label="3 B2 Cümle (her satıra 1)" value={draft.sentences} onChange={set('sentences')} multiline rows={5} />
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} className="rounded-none">
            <X size={16} weight="bold" className="mr-1.5" /> İptal
          </Button>
          <Button onClick={save} className="rounded-none btn-tactile">
            <FloppyDisk size={16} weight="bold" className="mr-1.5" /> Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
